import { NodeRunInstance } from "../../types/workflow"
import { VerticalLine } from "../../svgUrls";
import { useState, useEffect } from "react";
import { ExpandMore, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import HorizontalTabs from "../Common/HorizontalTabs";
import { formatDateTime } from "../../utils/misc";
import { FallbackSvg } from '../Common/FallbackSvg';
import { WorkflowServiceIcon } from "../Common/WorkflowServiceIcon";
import { 
    useAddOneShotSampleFromNodeRun, 
    useRemoveOneShotSampleFromNodeRun, 
    useCheckOneShotSampleExists 
} from "../../api/oneShotSampleApi";
import { Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { NodeRunInstanceState } from "../../constants/workflow";

type WorkflowHistoryNodeProps = {
    element: NodeRunInstance;
    elementIndex: number;
}

const getInputData = (inputData: any) => {
    try {
        return JSON.parse(inputData)
    } catch (e) {
        return { body: inputData }
    }
}

const WorkflowHistoryNode = ({
    element,
    elementIndex,
}: WorkflowHistoryNodeProps) => {
    console.log(element, "element  ");
    
    const [openFull, setOpenFull] = useState(false);
    const [openedTab, setOpenedTab] = useState("inputs");
    const [isSavedOneShotSample, setIsSavedOneShotSample] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { mutate: addOneShotSample } = useAddOneShotSampleFromNodeRun();
    const { mutate: removeOneShotSample } = useRemoveOneShotSampleFromNodeRun();
    const { mutate: checkOneShotSampleExists } = useCheckOneShotSampleExists();

    const dataToDisplay =
        openedTab === "inputs" && element.input_data
            ? getInputData(element.input_data)
            : element.data && JSON.parse(element.data) || {};

    const hasErrorInOutputs = openedTab === "outputs" && element.error !== "";

    // Determine if node passed or failed
    // A node is completed/successful if it's in Completed or Processed state and has no error
    const errorValue = element.error;
    const hasNoError = !errorValue || errorValue === "";
    const isCompleted = (element.state === NodeRunInstanceState.NodeRunInstanceCompleted || 
                         element.state === NodeRunInstanceState.NodeRunInstanceProcessed) && hasNoError;
    // A node is failed if it's in Failed state OR has an error
    const nodeHasError = errorValue && errorValue !== "";
    const isFailed = element.state === NodeRunInstanceState.NodeRunInstanceFailed || nodeHasError;

    useEffect(() => {
        if (element?.node?.action === 'Conversation' && element?.node?.service === 'chatgpt') {
            checkOneShotSampleExists({ nodeRunInstanceId: element.id }, {
                onSuccess: (existsData: { exists: boolean }) => {
                    setIsSavedOneShotSample(existsData?.exists || false);
                }
            });
        }
    }, [element, checkOneShotSampleExists]);

    const handleOneShotSampleToggle = () => {
        setIsLoading(true);
        
        if (isSavedOneShotSample) {
            removeOneShotSample({ nodeRunInstanceId: element.id }, {
                onSuccess: () => {
                    setIsSavedOneShotSample(false);
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                }
            });
        } else {
            addOneShotSample({ nodeRunInstanceId: element.id }, {
                onSuccess: () => {
                    setIsSavedOneShotSample(true);
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                }
            });
        }
    };

    const renderOneShotSampleCheckbox = () => {
        if (element?.node?.action === 'Conversation' && element?.node?.service === 'chatgpt') {
            return (
                <Tooltip title="Save as a one-shot sample for this node">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isSavedOneShotSample}
                                onChange={handleOneShotSampleToggle}
                                disabled={isLoading}
                                size="small"
                            />
                        }
                        label="Add retraining feedback"
                        className="text-xs text-greyishBlue"
                    />
                </Tooltip>
            );
        }
        return null;
    };

    return (
        <div className="flex flex-col items-center w-full">
            <div className="w-full text-xs p-2 border border-solid border-border-grey rounded-lg">
                <div className="flex justify-between cursor-pointer" onClick={() => setOpenFull(!openFull)}>
                    <div className="flex gap-4 items-center">
                        <WorkflowServiceIcon
                            workflowType={element?.node?.service || 'help'}
                            alt={element?.node?.service || 'node'}
                            height={40}
                            width={40}
                        />
                        <div className="flex-1">
                            <div className="font-medium">
                                {elementIndex+1}{". "}{element?.node?.name || 'Deleted node'}
                            </div>
                            <div className="text-greyishBlue">{formatDateTime(element.created_time)}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isFailed ? (
                            <ErrorIcon 
                                sx={{ color: '#D02B2B', fontSize: '16px' }} 
                                titleAccess="Node failed"
                            />
                        ) : isCompleted ? (
                            <CheckCircle 
                                sx={{ color: '#18855E', fontSize: '16px' }} 
                                titleAccess="Node completed successfully"
                            />
                        ) : null}
                        <ExpandMore fontSize="small" htmlColor="#94A1AF" />
                    </div>
                </div>
                {
                    openFull ?
                    <div className="mt-2">
                        {renderOneShotSampleCheckbox()}
                        <HorizontalTabs
                            tabArray={[
                                {
                                        id: 'inputs',
                                        value: 'Inputs'
                                },
                                {
                                        id: 'outputs',
                                        value: 'Outputs'
                                },
                            ]}
                            tabValue={openedTab}
                            setTabValue={(value) => setOpenedTab(value)}
                        />
                        <div>
                            {hasErrorInOutputs ? (
                                <div className="my-4">
                                    <div className="text-2xs text-red-600 mb-2">
                                        Error
                                    </div>
                                    <div className="p-3 text-xs bg-backgroundGrey max-h-[140px] overflow-auto rounded text-wrap break-words">
                                        {JSON.stringify(element.error)}
                                    </div>
                                </div>
                            ) : (
                                Object.entries(dataToDisplay).map(
                                    ([dataKey, dataValue]) => {
                                        return (
                                            <div className="my-4" key={dataKey}>
                                                <div className="text-2xs text-greyishBlue mb-2">
                                                    {dataKey}
                                                </div>
                                                <div className="p-3 text-xs bg-backgroundGrey max-h-[140px] overflow-auto rounded text-wrap break-words">
                                                    {JSON.stringify(dataValue)}
                                                </div>
                                            </div>
                                        );
                                    }
                                )
                            )}
                        </div>
                    </div>
                        : null
                }
            </div>
            <VerticalLine />
        </div>
    )
}

export default WorkflowHistoryNode;
